import React from 'react';
import { AircraftType, Airstrip, Customer, AdminSettings, Quote, Role, UserAccount } from '../types';
import { useQuoteView } from '../hooks/useQuoteView';
import AgentAdvisories from './AgentAdvisories';
import QuoteEditor from './QuoteView/QuoteEditor';
import QuoteList from './QuoteView/QuoteList';
import QuotePreview from './QuoteView/QuotePreview';
import { quoteApi } from '../services/quoteApi';
import { outboundService, OutboundState } from '../services/outboundService';
import { WhatsAppRequestHandoffPayload } from './InboxHub/whatsapp/whatsAppPanelTypes';

interface QuoteViewProps {
  aircrafts: AircraftType[];
  airstrips: Airstrip[];
  customers: Customer[];
  settings: AdminSettings;
  quotes: Quote[];
  setQuotes: React.Dispatch<React.SetStateAction<Quote[]>>;
  currentRole: Role;
  currentUser?: UserAccount | null;
  premiumMode?: boolean;
  onLog?: (module: any, action: string, details: string, severity: string) => void;
}

const QuoteView: React.FC<QuoteViewProps> = props => {
  const [showAdvisories, setShowAdvisories] = React.useState(false);
  const [previewQuote, setPreviewQuote] = React.useState<Quote | null>(null);
  const [manifestQuote, setManifestQuote] = React.useState<Quote | null>(null);
  const [manifestLoading, setManifestLoading] = React.useState(false);
  const [manifestData, setManifestData] = React.useState<any | null>(null);
  const [manifestError, setManifestError] = React.useState<string | null>(null);
  const [sendTarget, setSendTarget] = React.useState<Quote | null>(null);
  const [includeBreakdownDefault, setIncludeBreakdownDefault] = React.useState(true);
  const [includeBreakdown, setIncludeBreakdown] = React.useState(true);
  const [outboundState, setOutboundState] = React.useState<OutboundState | null>(null);
  const [outboundLoading, setOutboundLoading] = React.useState(false);
  const [outboundError, setOutboundError] = React.useState<string | null>(null);
  const [sendAccountId, setSendAccountId] = React.useState('');
  const [sendHtmlTemplateId, setSendHtmlTemplateId] = React.useState('');
  const [sendPdfTemplateId, setSendPdfTemplateId] = React.useState('');
  const [sendTo, setSendTo] = React.useState('');
  const [sendCc, setSendCc] = React.useState('');
  const [sendBcc, setSendBcc] = React.useState('');
  const [sendThreadMessageId, setSendThreadMessageId] = React.useState('');
  const [sendMode, setSendMode] = React.useState<'NEW' | 'SAME_THREAD'>('NEW');
  const [threadQuote, setThreadQuote] = React.useState<Quote | null>(null);
  const [threadLoading, setThreadLoading] = React.useState(false);
  const [threadData, setThreadData] = React.useState<any[]>([]);
  const [threadError, setThreadError] = React.useState<string | null>(null);
  const {
    isCreating,
    setIsCreating,
    formData,
    setFormData,
    handleIssuerDiscountChange,
    handleCeoDiscountChange,
    resetDiscountSource,
    startNewQuote,
    addLeg,
    autoReturnToBase,
    removeLeg,
    updateLeg,
    resolvedExtraServicesByLeg,
    weatherByCode,
    totals,
    advisories,
    canAuthorizeQuote,
    authorizeBlockReason,
    acknowledgeAdvisories,
    saveQuote,
    quotes,
    selectQuote,
    sendQuote,
    refreshZohoEstimateStatus,
    sleepOverAssessment,
    clearEditing,
  } = useQuoteView(props);

  const updateForm = (updates: Partial<typeof formData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const openSendModal = (quote: Quote) => {
    setSendTarget(quote);
    setIncludeBreakdown(includeBreakdownDefault);
  };

  const handleSelectQuote = async (quote: Quote) => {
    try {
      const full = await quoteApi.getQuote(quote.id);
      selectQuote(full);
    } catch (error) {
      console.error('Failed to load quote details:', error);
      selectQuote(quote);
    }
  };

  React.useEffect(() => {
    if (!sendTarget || outboundState || outboundLoading) return;
    setOutboundLoading(true);
    setOutboundError(null);
    outboundService
      .getState()
      .then(state => setOutboundState(state))
      .catch(error => {
        setOutboundError(error?.response?.data?.error || error?.message || 'Failed to load outbound config.');
      })
      .finally(() => setOutboundLoading(false));
  }, [sendTarget, outboundState, outboundLoading]);

  React.useEffect(() => {
    const quoteId = localStorage.getItem('ma_open_quote_id');
    if (!quoteId) return;
    const target = (props.quotes || []).find(q => q.id === quoteId);
    if (!target) return;
    localStorage.removeItem('ma_open_quote_id');
    handleSelectQuote(target).catch(() => {});
  }, [props.quotes]);

  React.useEffect(() => {
    const raw = localStorage.getItem('ma_whatsapp_handoff_QUOTER');
    if (!raw) return;
    if (!props.customers || props.customers.length === 0) return;
    try {
      const payload = JSON.parse(raw) as WhatsAppRequestHandoffPayload;
      const canonicalizeAirstripCode = (value?: string | null) => {
        const code = String(value || '').trim().toUpperCase();
        if (!code) return '';
        if (code === 'ARK' || code === 'HTAR' || code === 'ARUSHA') return 'HTAR';
        return code;
      };
      const normalize = (value?: string | null) => String(value || '').trim().toLowerCase();
      const payloadName = normalize(payload.customerName);
      const payloadPhone = normalize(payload.customerPhone).replace(/[^0-9]/g, '');
      const matchedCustomerId =
        payload.customerId ||
        props.customers.find((customer) => {
          const customerName = normalize(customer.name);
          const customerEmail = normalize(customer.email);
          const customerCompany = normalize(customer.company);
          const customerPhone = normalize(customer.phoneNumber).replace(/[^0-9]/g, '');
          const nameMatch = payloadName
            ? (customerName === payloadName || customerName.includes(payloadName) || payloadName.includes(customerName))
            : false;
          const phoneMatch = payloadPhone ? (customerPhone === payloadPhone) : false;
          return nameMatch || phoneMatch || (payloadName && customerEmail === payloadName) || (payloadName && customerCompany === payloadName);
        })?.id;
      const passengerCount = payload.passengerCount || 1;
      const departureIso = payload.isoDate || new Date().toISOString();
      const departureDate = departureIso.slice(0, 10);
      const departureTime = departureIso.includes('T') ? departureIso.slice(11, 16) : '08:00';
      setIsCreating(true);
      setFormData(prev => ({
        ...prev,
        customerId: matchedCustomerId || prev.customerId,
        date: departureDate,
        legs: [
          {
            ...prev.legs[0],
            id: `leg-${Date.now()}`,
            from: canonicalizeAirstripCode(payload.fromCode || prev.legs[0]?.from || ''),
            to: canonicalizeAirstripCode(payload.toCode || prev.legs[0]?.to || ''),
            paxCount: passengerCount,
            paxPick: passengerCount,
            paxOB: passengerCount,
            paxDrop: 0,
            etd: departureTime,
            eta: prev.legs[0]?.eta || '09:00',
          },
        ],
      }));
    } catch (error) {
      console.error('Failed to restore WhatsApp charter handoff', error);
    } finally {
      localStorage.removeItem('ma_whatsapp_handoff_QUOTER');
    }
  }, [setFormData, setIsCreating, props.customers]);

  React.useEffect(() => {
    if (!sendTarget || !outboundState) return;
    const routing = outboundState.routing?.find(item => item.usage === 'QUOTE_APPROVAL')
      || outboundState.routing?.find(item => item.usage === 'QUOTES');
    setSendAccountId(routing?.accountId || '');
    setSendHtmlTemplateId(routing?.htmlTemplateId || '');
    setSendPdfTemplateId(routing?.pdfTemplateId || '');
    setSendTo((routing?.defaultTo || '').trim());
    setSendCc((routing?.ccEmails || '').trim());
    setSendBcc((routing?.bccEmails || '').trim());
    setSendThreadMessageId('');
    setSendMode('SAME_THREAD');
  }, [sendTarget, outboundState]);

  const activeAccounts = outboundState?.accounts?.filter(account => account.enabled) || [];
  const htmlTemplates = outboundState?.templates?.filter(t => t.type === 'HTML' && t.isActive) || [];
  const pdfTemplates = outboundState?.templates?.filter(t => t.type === 'PDF' && t.isActive) || [];
  const approvalHtmlTemplates = htmlTemplates.filter(template => {
    const body = String(template.body || '');
    const name = String(template.name || '');
    const subject = String(template.subject || '');
    return (
      body.includes('{{approvalLink}}') ||
      body.includes('{{approvalForm}}') ||
      body.includes('{{quoteSummary}}') ||
      /quote approval|review\s*&?\s*approve/i.test(`${name} ${subject}`)
    );
  });
  const approvalPdfTemplates = pdfTemplates.filter(template => /quote approval|charter quote/i.test(String(template.name || '')));

  const manifestPassengers = Array.isArray(manifestData?.Passengers)
    ? manifestData.Passengers
    : Array.isArray(manifestData?.passengers)
    ? manifestData.passengers
    : [];
  const hasManifestPassengers = manifestPassengers.length > 0;
  const manifestIssued = !!manifestData?.issuedAt;

  const openThreadModal = async (quote: Quote) => {
    setThreadQuote(quote);
    setThreadLoading(true);
    setThreadData([]);
    setThreadError(null);
    try {
      const data = await quoteApi.getEmailThread(quote.id);
      setThreadData(Array.isArray(data) ? data : []);
    } catch (error: any) {
      setThreadError(error?.response?.data?.error || error?.message || 'Failed to load email thread.');
    } finally {
      setThreadLoading(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1520px] space-y-10 px-6 pb-24 pt-2 animate-in fade-in 2xl:max-w-[1680px]">
      {!isCreating ? (
        <QuoteList
          quotes={quotes}
          premiumMode={props.premiumMode}
          onNewQuote={startNewQuote}
          onSelectQuote={handleSelectQuote}
          onViewQuote={setPreviewQuote}
          onRequestSend={openSendModal}
          onViewPassengers={async (quote) => {
            setManifestQuote(quote);
            setManifestLoading(true);
            setManifestError(null);
            try {
              const data = await quoteApi.getPassengerManifest(quote.id);
              setManifestData(data);
            } catch (error: any) {
              setManifestData(null);
              setManifestError(error?.response?.data?.error || error?.message || 'Failed to load passenger list.');
            } finally {
              setManifestLoading(false);
            }
          }}
        />
      ) : (
        <div className="space-y-6">
          {props.settings.agent?.enabled &&
            props.settings.agent.modules?.QUOTER &&
            props.settings.agent.rolesByModule?.QUOTER?.includes(props.currentRole) && (
              <div className="rounded-[1.5rem] border border-slate-200 bg-slate-900/95 px-6 py-3 text-white shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-emerald-300">
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-400/20 text-emerald-300">▲</span>
                    <span>Supervisor Agent</span>
                    <span className="text-slate-400">|</span>
                    <span className="text-slate-300">{advisories.length} Advisory</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={acknowledgeAdvisories}
                      className="rounded-full border border-slate-700 bg-slate-800 px-3 py-1 text-[10px] font-black uppercase tracking-[0.3em] text-white"
                    >
                      Acknowledge All
                    </button>
                    <button
                      onClick={() => setShowAdvisories(prev => !prev)}
                      className="rounded-full border border-slate-700 bg-slate-800 px-3 py-1 text-[10px] font-black uppercase tracking-[0.3em] text-white"
                    >
                      {showAdvisories ? 'Collapse' : 'Expand'}
                    </button>
                  </div>
                </div>
                {showAdvisories && (
                  <div className="mt-4 rounded-[1.25rem] border border-slate-700 bg-slate-800/80 p-4">
                    <AgentAdvisories
                      advisories={advisories}
                      mode="INLINE"
                      moduleLabel="Charter"
                      onAcknowledge={acknowledgeAdvisories}
                      showEmptyState
                    />
                  </div>
                )}
              </div>
            )}

          <QuoteEditor
            aircrafts={props.aircrafts}
            airstrips={props.airstrips}
            customers={props.customers}
            settings={props.settings}
            currentRole={props.currentRole}
            currentUser={props.currentUser}
            formData={formData}
            weatherByCode={weatherByCode}
            totals={totals}
            advisories={advisories}
            onClose={() => {
              setIsCreating(false);
              clearEditing();
            }}
            onUpdateForm={updateForm}
            onResetDiscountSource={resetDiscountSource}
            onIssuerDiscountChange={handleIssuerDiscountChange}
            onCeoDiscountChange={handleCeoDiscountChange}
            onAddLeg={addLeg}
            onAutoReturn={autoReturnToBase}
            onRemoveLeg={removeLeg}
            onUpdateLeg={updateLeg}
            resolvedExtraServicesByLeg={resolvedExtraServicesByLeg}
            onSave={saveQuote}
            canAuthorizeQuote={canAuthorizeQuote}
            authorizeBlockReason={authorizeBlockReason}
            includeBreakdownDefault={includeBreakdownDefault}
            onToggleIncludeBreakdown={setIncludeBreakdownDefault}
            sleepOverAssessment={sleepOverAssessment}
          />
        </div>
      )}
      {previewQuote && (
        <QuotePreview
          quote={previewQuote}
          onClose={() => setPreviewQuote(null)}
          onEdit={async () => {
            setPreviewQuote(null);
            await handleSelectQuote(previewQuote);
          }}
          onSend={() => openSendModal(previewQuote)}
          onRefreshZoho={async () => {
            const updated = await refreshZohoEstimateStatus(previewQuote.id);
            if (updated) {
              setPreviewQuote(updated);
            }
          }}
          onViewThread={() => openThreadModal(previewQuote)}
        />
      )}

      {threadQuote && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-6">
          <div className="w-full max-w-5xl rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-indigo-600 font-semibold">Email Thread</p>
                <h3 className="text-xl font-bold text-slate-900">
                  {threadQuote.customerName || 'Client'}
                </h3>
                <div className="mt-1 text-xs text-slate-500">
                  Quote: {threadQuote.quoteNumber || '—'} · Booking: {threadQuote.bookingId || '—'} · PNR: {threadQuote.pnrCode || '—'}
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setThreadQuote(null);
                  setThreadData([]);
                  setThreadError(null);
                }}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-500 hover:text-slate-900"
              >
                ✕
              </button>
            </div>
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              {threadLoading ? (
                <div className="text-sm text-slate-500">Loading email thread…</div>
              ) : threadError ? (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  {threadError}
                </div>
              ) : threadData.length === 0 ? (
                <div className="text-sm text-slate-500">No email thread found for this quote yet.</div>
              ) : (
                <div className="space-y-6">
                  {threadData.map((thread: any) => {
                    const messages = Array.isArray(thread?.EmailMessages) ? thread.EmailMessages : [];
                    return (
                      <div key={thread.id} className="rounded-xl border border-slate-200">
                        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
                          <div className="text-xs font-semibold text-slate-700">
                            Subject: {thread.subject || '—'}
                          </div>
                          <div className="text-[11px] text-slate-500 mt-1">
                            Customer Email: {thread.customerEmail || '—'} · Last Activity:{' '}
                            {thread.lastMessageAt ? new Date(thread.lastMessageAt).toLocaleString() : '—'}
                          </div>
                        </div>
                        <div className="divide-y divide-slate-100">
                          {messages.map((msg: any) => {
                            const timestamp = msg.sentAt || msg.receivedAt || msg.createdAt;
                            const direction = msg.direction || 'UNKNOWN';
                            return (
                              <div key={msg.id} className="px-4 py-3">
                                <div className="flex flex-wrap items-center gap-2 text-[11px]">
                                  <span
                                    className={`rounded-full px-2 py-0.5 font-semibold ${
                                      direction === 'INBOUND'
                                        ? 'bg-emerald-100 text-emerald-700'
                                        : 'bg-indigo-100 text-indigo-700'
                                    }`}
                                  >
                                    {direction}
                                  </span>
                                  <span className="text-slate-600">{msg.fromEmail || '—'}</span>
                                  <span className="text-slate-400">→</span>
                                  <span className="text-slate-600">{msg.toEmails || '—'}</span>
                                  <span className="text-slate-400">•</span>
                                  <span className="text-slate-500">
                                    {timestamp ? new Date(timestamp).toLocaleString() : '—'}
                                  </span>
                                </div>
                                <div className="mt-1 text-xs text-slate-700 font-medium">
                                  {msg.subject || 'No subject'}
                                </div>
                                {msg.messageId && (
                                  <div className="mt-1 text-[11px] text-slate-500 break-all">
                                    Message-ID: {msg.messageId}
                                  </div>
                                )}
                                {(msg.textBody || msg.htmlBody) && (
                                  <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 whitespace-pre-wrap">
                                    {msg.textBody || String(msg.htmlBody || '').replace(/<[^>]*>/g, ' ').slice(0, 1200)}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {manifestQuote && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-6">
          <div className="w-full max-w-4xl rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-600 font-semibold">Passenger List</p>
                <h3 className="text-xl font-bold text-slate-900">
                  {manifestQuote.customerName || 'Client'}
                </h3>
                <div className="mt-1 text-xs text-slate-500">
                  Quote: {manifestQuote.quoteNumber || '—'} · Booking: {manifestQuote.bookingId || '—'} · PNR: {manifestQuote.pnrCode || '—'}
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setManifestQuote(null);
                  setManifestData(null);
                  setManifestError(null);
                }}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-500 hover:text-slate-900"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              {manifestLoading ? (
                <div className="text-sm text-slate-500">Loading passenger list...</div>
              ) : manifestError ? (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  {manifestError}
                </div>
              ) : !manifestData || !hasManifestPassengers ? (
                <div className="space-y-2 text-sm text-slate-500">
                  <div>No passenger list submitted yet.</div>
                  {manifestData?.submittedAt ? (
                    <div>Submitted at: {new Date(manifestData.submittedAt).toLocaleString()}</div>
                  ) : (
                    <div>Passenger list is pending from client.</div>
                  )}
                  {manifestData?.notes && (
                    <div className="text-slate-600">Notes: {manifestData.notes}</div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <div className="text-xs text-slate-600">
                      <div>Submitted: {manifestData?.submittedAt ? new Date(manifestData.submittedAt).toLocaleString() : '—'}</div>
                      {manifestIssued && (
                        <div>
                          Issued: {new Date(manifestData.issuedAt).toLocaleString()}
                          {manifestData?.issuedBy ? ` · by ${manifestData.issuedBy}` : ''}
                        </div>
                      )}
                    </div>
                    <span className="rounded-full border border-slate-200 bg-white px-4 py-2 text-[10px] font-black uppercase tracking-[0.2em] text-slate-600">
                      Issue in Planner Matrix
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="bg-slate-900 text-white">
                          <th className="text-left px-3 py-2">Name</th>
                          <th className="text-left px-3 py-2">Nationality</th>
                          <th className="text-left px-3 py-2">Passport No.</th>
                          <th className="text-left px-3 py-2">Status</th>
                          <th className="text-left px-3 py-2">Baggage</th>
                          <th className="text-left px-3 py-2">Comment</th>
                          <th className="text-left px-3 py-2">Phone</th>
                        </tr>
                      </thead>
                      <tbody>
                        {manifestPassengers.map((p: any) => (
                          <tr key={p.id} className="border-b border-slate-200">
                            <td className="px-3 py-2">{p.fullName || '-'}</td>
                            <td className="px-3 py-2">{p.nationality || '-'}</td>
                            <td className="px-3 py-2">{p.passportNumber || '-'}</td>
                            <td className="px-3 py-2">{p.status || '-'}</td>
                            <td className="px-3 py-2">{p.baggageWeight || '-'}</td>
                            <td className="px-3 py-2">{p.comment || '-'}</td>
                            <td className="px-3 py-2">{p.phone || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {sendTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] font-semibold text-indigo-600 uppercase tracking-[0.2em]">Send Quote</p>
                <h3 className="text-lg font-bold text-slate-900 mt-1">{sendTarget.customerName || 'Charter Quote'}</h3>
                <p className="text-sm text-slate-500 mt-1">
                  Booking: {sendTarget.bookingId || 'Pending'} · PNR: {sendTarget.pnrCode || 'Pending'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSendTarget(null)}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600"
              >
                Close
              </button>
            </div>

            <div className="mt-5 space-y-4">
              {outboundError && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  {outboundError}
                </div>
              )}

              <label className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={includeBreakdown}
                  onChange={e => setIncludeBreakdown(e.target.checked)}
                />
                Include detailed cost breakdown in the email/PDF
              </label>
              <div className="text-xs text-slate-500">
                If disabled, the client receives a clean summary total only.
              </div>

              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                    Email Account
                  </label>
                  <select
                    value={sendAccountId}
                    onChange={e => setSendAccountId(e.target.value)}
                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800"
                    disabled={outboundLoading}
                  >
                    <option value="">Use routing default</option>
                    {activeAccounts.map(account => (
                      <option key={account.id} value={account.id}>
                        {account.label} ({account.fromEmail || account.username})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                    To
                  </label>
                  <input
                    value={sendTo}
                    onChange={e => setSendTo(e.target.value)}
                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800"
                    placeholder="client@example.com"
                  />
                </div>

                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                    CC
                  </label>
                  <input
                    value={sendCc}
                    onChange={e => setSendCc(e.target.value)}
                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800"
                    placeholder="ops@example.com, dfo@example.com"
                  />
                </div>

                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                    BCC
                  </label>
                  <input
                    value={sendBcc}
                    onChange={e => setSendBcc(e.target.value)}
                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800"
                    placeholder="archive@example.com"
                  />
                </div>

                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                    Delivery Mode
                  </label>
                  <select
                    value={sendMode}
                    onChange={e => setSendMode(e.target.value === 'SAME_THREAD' ? 'SAME_THREAD' : 'NEW')}
                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800"
                  >
                    <option value="NEW">New Email (SMTP)</option>
                    <option value="SAME_THREAD">Same Thread (Gmail API via Bridge)</option>
                  </select>
                  <div className="mt-1 text-[10px] text-slate-500">
                    `Same Thread` uses API bridge + Gmail threadId from inbound email; `New Email` uses direct SMTP.
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                    Thread Message-ID (Optional)
                  </label>
                  <input
                    value={sendThreadMessageId}
                    onChange={e => setSendThreadMessageId(e.target.value)}
                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800"
                    placeholder="<message-id@clientmail>"
                  />
                  <div className="mt-1 text-[10px] text-slate-500">
                    Use client inbound Message-ID to continue the same email conversation.
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                    HTML Template
                  </label>
                  <select
                    value={sendHtmlTemplateId}
                    onChange={e => setSendHtmlTemplateId(e.target.value)}
                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800"
                    disabled={outboundLoading}
                  >
                    <option value="">Use routing default</option>
                    {approvalHtmlTemplates.map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                    PDF Template
                  </label>
                  <select
                    value={sendPdfTemplateId}
                    onChange={e => setSendPdfTemplateId(e.target.value)}
                    className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800"
                    disabled={outboundLoading}
                  >
                    <option value="">Use routing default</option>
                    {approvalPdfTemplates.map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setSendTarget(null)}
                className="rounded-full border border-slate-200 px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-600"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  sendQuote(sendTarget.id, {
                    includeBreakdown,
                    sendMode,
                    accountId: sendAccountId || undefined,
                    htmlTemplateId: sendHtmlTemplateId || undefined,
                    pdfTemplateId: sendPdfTemplateId || undefined,
                    to: sendTo || undefined,
                    cc: sendCc || undefined,
                    bcc: sendBcc || undefined,
                    threadMessageId: sendThreadMessageId || undefined,
                  });
                  setSendTarget(null);
                }}
                className="rounded-full bg-indigo-600 px-5 py-2 text-xs font-bold uppercase tracking-wider text-white"
                disabled={outboundLoading}
              >
                Send to Client
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuoteView;
